import { logger } from '@/services/logger';
import { DataModelAttribute, Item } from '@/types/DataModel';

const TEMPLATE_DIMENSIONAL = 'DIMENSIONAL';
const TEMPLATE_IMAGE = 'IMAGE';
const TEMPLATE_TABLE = 'TABLE';
const TEMPLATE_TEXT = 'TEXT';

const toJson = (itemResult: any) => {
    const result = { ...itemResult };
    for (const key in result) {
        const val = result[key];
        if (typeof val === 'string') {
            try {
                result[key] = JSON.parse(val);
            } catch (error) {
                result[key] = val;
            }
        }
    }
    return result;
};
const isAttributeAlreadyAdded = (array: any, attribute: any) => {
    return array.some((item: any) => item.name === attribute.name);
};

const mergeAttributes = async (sectionsA: any, sectionsB: any): Promise<any[]> => {
    return new Promise<any[]>((resolve, reject) => {
        try {
            const attributes = [];

            const maxLength = Math.max(sectionsA.length, sectionsB.length);
            for (
                let sectionIndex = 0;
                sectionIndex < maxLength;
                sectionIndex++
            ) {
                const secA = sectionsA[sectionIndex] || { rows: [] };
                const secB = sectionsB[sectionIndex] || { rows: [] };
                const maxRows = Math.max(
                    secA.rows.length,
                    secB.rows.length,
                );

                for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
                    const rowA = secA.rows[rowIndex] || [];
                    const rowB = secB.rows[rowIndex] || [];
                    const maxAttrs = Math.max(rowA.length, rowB.length);

                    for (
                        let attrIndex = 0;
                        attrIndex < maxAttrs;
                        attrIndex++
                    ) {
                        const attrA = rowA[attrIndex];
                        const attrB = rowB[attrIndex];

                        if (
                            attrA &&
                            !isAttributeAlreadyAdded(attributes, attrA)
                        ) {
                            attributes.push(attrA);
                        }
                        if (
                            attrB &&
                            !isAttributeAlreadyAdded(attributes, attrB)
                        ) {
                            attributes.push(attrB);
                        }
                    }
                }
            }
            resolve(attributes);
        } catch (error) {
            reject(error);
        }
    });
};

const loadAdditionalSectionAttributes = (layout: string, item: any, AdditionalCategoryAttributeService: any, rootScope: any, currentLayout: string): Promise<any[]> => {
    return new Promise<any[]>((resolve, reject) => {
        AdditionalCategoryAttributeService.loadAdditionalSectionAttributes(
            layout,
            item,
            (additionalSectionAttributes: any) => {

                const sections = rootScope.dataModel.filteredSections(
                    layout,
                    item,
                    additionalSectionAttributes,
                );
                let rowLength = 0;
                sections.forEach(
                    (section: any) => {
                        section.rows = [];
                        let row: any[] = [];
                        section.attributes.map((attribute: any) => {
                            rootScope.setInputRenderer(attribute);
                            rootScope.setSectionAttributeParam(
                                currentLayout,
                                section.name,
                                attribute,
                                'readonly',
                                null,
                                null,
                            );
                            rootScope.setSectionAttributeParam(
                                currentLayout,
                                section.name,
                                attribute,
                                'labelWidth',
                                4,
                                null,
                            );
                            rootScope.setSectionAttributeParam(
                                currentLayout,
                                section.name,
                                attribute,
                                'inputWidth',
                                8,
                                null,
                            );
                            attribute.isComplexType =
                                attribute.typeName == 'Collection' ||
                                attribute.typeName == 'MultiDimensional' ||
                                attribute.typeName == 'MultiReference';
                            if (attribute.isComplexType) {
                                attribute.inputWidth +=
                                    attribute.labelWidth;
                                attribute.labelWidth = 0;
                            }
                            rowLength +=
                                attribute.labelWidth + attribute.inputWidth;
                            row.push(attribute);
                            if (rowLength >= 12) {
                                section.rows.push(row);
                                rowLength = 0;
                                row = [];
                            }
                        });

                    },
                    (error: any) => {
                        logger.error(
                            'compare-items-dialog-utils: Error loading additional section attributes:',
                            error,
                        );
                        reject(error);
                    },
                );
                resolve(sections);
            },
        );
    });
};
const prepareAttributeTemplates = (attributes: any[], firstItem: any, secondItem: any, dataModel: any) => {
    attributes.forEach((attribute: any) => {
        switch (attribute.template) {
            case 'Image':
                attribute.comparisonTemplate = TEMPLATE_IMAGE;
                break;
            case 'Dimensional':
            case 'MultiLineDimensional':
                attribute.comparisonTemplate = TEMPLATE_DIMENSIONAL;
                break;
            case 'MultiDimensional':
            case 'Collection':
            case 'MultiReference':
                attribute.comparisonTemplate = TEMPLATE_TABLE;
                attribute.collections = prepareTableAttribute(attribute, firstItem, secondItem, dataModel);
                break;
            default:
                attribute.comparisonTemplate = TEMPLATE_TEXT;
        }
    });
};

const isAttributeHidden = (attribute: DataModelAttribute, item: Item): boolean => {
    if (!attribute) return false;

    const { name, additionalParams } = attribute;

    // Hidden via datamodel
    if (additionalParams?.hidden) return true;

    //  Hidden via item.attributeStates__
    if (item.attributeStates__?.includes(`${name}:hidden`)) {
        return true;
    }

    return false;
};

const ValueToString = (attribute: any, item: any, physicalAttributeService: any, rootScope: any) => {
    try {
        switch (attribute.template) {
            case 'String':
            case 'Integer':
            case 'Date':
            case 'Float':
                return item[attribute.name] || ' ';

            case 'Enum':
            case 'OpenEnum': {
                if (item[attribute.name]) {
                    const selectedOption = attribute.options?.find((opt: any) => opt.key === item[attribute.name].toString());
                    return selectedOption ? selectedOption.value : ' ';
                }
                return ' ';
            }
            case 'Physical':
                if (item[attribute.name]) {
                    const [valStr, unit] = item[attribute.name].split(' ');
                    let translatedUnit = unit;
                    if (Array.isArray(attribute.options)) {
                        const option = attribute.options.find((opt: any) => opt.key === unit);
                        if (option) {
                            translatedUnit = option.value || option.key;
                        }
                    } else if (Array.isArray(attribute.params?.keys)) {
                        const param = attribute.params.keys.find((opt: any) => opt.key === unit);
                        if (param) {
                            translatedUnit = param.value;
                        }
                    }
                    return (`${physicalAttributeService.formatValue(valStr, unit)} ${translatedUnit}` || ' ');
                } else {
                    return ' ';
                }
            case 'Boolean': {
                const selectedValue = attribute.params.values.find(
                    (opt: any) => opt.key === item[attribute.name],
                );
                return selectedValue
                    ? selectedValue.value === 'True'
                        ? '\u2713'
                        : '\u2718'
                    : ' ';
            }

            case 'Dimensional':
            case 'MultiLineDimensional': {
                const options = rootScope.dataModel.getAttributeOptions(attribute);
                const selectedOption = options.find((option: any) => {
                    attribute.dimensionalOption = option;
                    return item[attribute.name] && item[attribute.name][option.key];
                });
                const value = selectedOption ? item[attribute.name] : ' ';
                return value || ' ';
            }
            case 'EnumSet':
            case 'OpenEnumSet':
                return item[attribute.name]
                    ? item[attribute.name].join(', ')
                    : ' ';
            default:
                return item[attribute.name]?.toString();
        }

    } catch (error) {
        logger.error('compare-items-dialog-utils: Error occurred while formatting attribute:', error);
        return ('Error');
    }
};
const prepareTableAttribute = (attribute: any, firstItems: any, secondItems: any, dataModel: any) => {
    const collections: {
        first: any,
        second: any
    } = {
        first: {},
        second: {}
    };
    attribute.members = dataModel.getMemberAttributes(attribute);
    collections.first[attribute.name] = firstItems[attribute.name] || [];
    collections.second[attribute.name] = secondItems[attribute.name] || [];
    const maxLength = Math.max(
        collections.first[attribute.name].length,
        collections.second[attribute.name].length,
    );
    if (maxLength != collections.first[attribute.name].length) {
        for (let k = 0; k < maxLength - collections.first[attribute.name].length; k++) {
            collections.first[attribute.name].push({});
        }
    }

    for (let i = 0; i < maxLength; i++) {
        collections.first[attribute.name][i] = collections.first[attribute.name][i] || {};
        collections.second[attribute.name][i] = collections.second[attribute.name][i] || {};

        const firstItemEntry = collections.first[attribute.name][i];
        const secondItemEntry = collections.second[attribute.name][i];

        for (let j = 0; j < attribute.members.length; j++) {
            const memberAttribute = attribute.members[j].name;
            const hasAttribute =
                !(memberAttribute in firstItemEntry || memberAttribute in secondItemEntry);
            firstItemEntry[memberAttribute] = {
                isEqual:
                    hasAttribute ||
                    firstItemEntry[memberAttribute] ===
                    secondItemEntry[memberAttribute],
                val: (!(firstItemEntry[memberAttribute]) ? '-' : firstItemEntry[memberAttribute])
            };
            secondItemEntry[memberAttribute] = {
                isEqual: firstItemEntry[memberAttribute].isEqual,
                val: (!(secondItemEntry[memberAttribute]) ? '-' : secondItemEntry[memberAttribute])
            };
        }
    }

    return collections;
};

export { isAttributeHidden, loadAdditionalSectionAttributes, mergeAttributes, prepareAttributeTemplates, toJson, ValueToString };
